import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navigation, Copy, ArrowRight, Train, Plane } from "lucide-react";
import { useTrip, useUI } from "@/lib/store";
import { RouteVisual } from "@/components/RouteVisual";
import { copyText, mapsUrl } from "@/components/DriverCard";
import { fmtDayMonth } from "@/lib/dates";

const PROVIDERS = [["apple", "Apple Maps"], ["google", "Google Maps"], ["amap", "Amap / Gaode"], ["baidu", "Baidu Maps"]];

export default function MapView() {
  const { trip, updateSettings, organiser } = useTrip();
  const { showDriver } = useUI();
  const navigate = useNavigate();
  const [active, setActive] = useState("hong_kong");
  const dest = trip.destinations.find((d) => d.id === active);
  const hotels = trip.hotels.filter((h) => h.destinationId === active);
  const legs = trip.transports.filter((t) => t.type === "train" || t.id === "tr_flight_out");
  const provider = trip.settings.mapsProvider || "apple";

  return (
    <div className="pb-8">
      <p className="eyebrow mt-3">Journey</p>
      <h1 className="font-display text-3xl font-semibold">The route</h1>
      <div data-testid="schematic-route-map" className="card-soft mt-3 p-3"><RouteVisual activeId={active} onSelect={setActive} /></div>

      <div className="mt-4 space-y-2">
        {legs.map((t) => (
          <button key={t.id} data-testid={`map-leg-${t.id}`} onClick={() => navigate("/more/transport")} className="tap card-soft flex w-full items-center gap-3 p-3 text-left">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--jade-soft)] text-[var(--jade)]">{t.type === "flight" ? <Plane className="h-4 w-4" /> : <Train className="h-4 w-4" />}</span>
            <span className="flex-1 text-sm"><span className="font-semibold">{t.fromCity} → {t.toCity}</span><span className="block text-xs text-[var(--ink-muted)]">{fmtDayMonth(t.date)}{t.duration ? ` · ${t.duration}` : ""}{!t.departTime ? " · time TBC" : ""}</span></span>
            <ArrowRight className="h-4 w-4 text-[var(--ink-muted)]" />
          </button>
        ))}
      </div>

      {dest && (
        <div data-testid={`map-destination-${dest.id}`} className="mt-5 overflow-hidden rounded-3xl border bg-white">
          <div className="relative h-40"><img src={dest.image} alt={dest.name} className="h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-[#0F2620]/80 to-transparent" />
            <div className="absolute bottom-3 left-4 text-[var(--parchment)]"><p className="eyebrow !text-[var(--parchment)]/80">{dest.dates.length - 1} night{dest.dates.length > 2 ? "s" : ""} · {fmtDayMonth(dest.dates[0])} → {fmtDayMonth(dest.dates[dest.dates.length - 1])}</p><h2 className="font-display text-3xl font-semibold">{dest.name} <span className="font-cn text-xl font-normal">{dest.chinese}</span></h2></div></div>
          <div className="p-4">
            <p className="text-sm text-[var(--ink-muted)]">{dest.tagline}</p>
            {hotels.map((h) => (
              <div key={h.id} className="mt-3 rounded-2xl bg-[var(--parchment-2)] p-3">
                <p className="font-semibold">{h.name}</p>
                <p className="font-cn text-base">{h.addressChinese}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button data-testid={`map-driver-${h.id}`} onClick={() => showDriver(h)} className="tap inline-flex items-center gap-1 rounded-full bg-[var(--jade)] px-3 py-1.5 text-xs font-semibold text-[var(--parchment)]"><Navigation className="h-3.5 w-3.5" /> Show driver</button>
                  <button data-testid={`map-copy-${h.id}`} onClick={() => copyText(`${h.addressChinese}\n${h.address}`, "Address copied")} className="tap inline-flex items-center gap-1 rounded-full border bg-white px-3 py-1.5 text-xs font-semibold"><Copy className="h-3.5 w-3.5" /> Copy</button>
                  <a data-testid={`map-open-${h.id}`} href={mapsUrl(provider, h)} target="_blank" rel="noreferrer" className="tap inline-flex items-center gap-1 rounded-full border bg-white px-3 py-1.5 text-xs font-semibold">Open in {PROVIDERS.find((p) => p[0] === provider)?.[1]}</a>
                </div>
              </div>
            ))}
            <button data-testid="map-open-days" onClick={() => navigate(`/trip?date=${dest.dates[0]}`)} className="tap mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[var(--jade)]">See days in {dest.name} <ArrowRight className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      {organiser && (
        <div className="mt-5 card-soft p-4">
          <p className="eyebrow">Maps provider</p>
          <div className="mt-2 flex flex-wrap gap-2">{PROVIDERS.map(([v, l]) => <button key={v} data-testid={`maps-provider-${v}`} onClick={() => updateSettings({ mapsProvider: v })} className={`tap rounded-full border px-3 py-1.5 text-xs font-semibold ${provider === v ? "bg-[var(--jade)] text-[var(--parchment)]" : "bg-white"}`}>{l}</button>)}</div>
        </div>
      )}
    </div>
  );
}
